export const TEXT_FILE_PREVIEW_MAX_SIZE_BYTES = 5 * 1024 * 1024
export const TEXT_FILE_SYNTAX_HIGHLIGHT_MAX_SIZE_BYTES = 64 * 1024

export type TextFilePreviewSyntaxLanguage =
  | 'bash'
  | 'c'
  | 'cpp'
  | 'csharp'
  | 'css'
  | 'diff'
  | 'go'
  | 'graphql'
  | 'ini'
  | 'java'
  | 'javascript'
  | 'json'
  | 'kotlin'
  | 'less'
  | 'lua'
  | 'makefile'
  | 'markdown'
  | 'php'
  | 'python'
  | 'ruby'
  | 'rust'
  | 'scss'
  | 'sql'
  | 'swift'
  | 'typescript'
  | 'vbnet'
  | 'xml'
  | 'yaml'

const GENERIC_MIME_TYPES = new Set(['application/octet-stream', 'binary/octet-stream'])

const TEXT_MIME_TYPES = new Set([
  'application/csv',
  'application/graphql',
  'application/javascript',
  'application/json',
  'application/ld+json',
  'application/ndjson',
  'application/sql',
  'application/toml',
  'application/typescript',
  'application/x-httpd-php',
  'application/x-javascript',
  'application/x-ndjson',
  'application/x-sh',
  'application/x-yaml',
  'application/xml',
  'application/yaml'
])

const TEXT_MIME_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  '.csv': 'text/csv',
  '.css': 'text/css',
  '.htm': 'text/html',
  '.html': 'text/html',
  '.js': 'application/javascript',
  '.json': 'application/json',
  '.jsonl': 'application/x-ndjson',
  '.jsx': 'text/plain',
  '.md': 'text/markdown',
  '.mdx': 'text/markdown',
  '.mjs': 'application/javascript',
  '.ndjson': 'application/x-ndjson',
  '.toml': 'application/toml',
  '.ts': 'text/plain',
  '.tsv': 'text/tab-separated-values',
  '.txt': 'text/plain',
  '.xhtml': 'application/xhtml+xml',
  '.xml': 'application/xml',
  '.yaml': 'application/yaml',
  '.yml': 'application/yaml'
}

const TEXT_EXTENSIONS = new Set([
  ...Object.keys(TEXT_MIME_TYPE_BY_EXTENSION),
  '.asm',
  '.astro',
  '.bash',
  '.bat',
  '.c',
  '.cc',
  '.cfg',
  '.cjs',
  '.conf',
  '.config',
  '.cpp',
  '.cs',
  '.cshtml',
  '.csh',
  '.csproj',
  '.cxx',
  '.dart',
  '.diff',
  '.dockerignore',
  '.editorconfig',
  '.env',
  '.fs',
  '.fsproj',
  '.gitignore',
  '.gitattributes',
  '.gitconfig',
  '.go',
  '.gradle',
  '.graphql',
  '.gql',
  '.h',
  '.hh',
  '.hpp',
  '.hxx',
  '.ini',
  '.java',
  '.kt',
  '.kts',
  '.less',
  '.log',
  '.lua',
  '.manifest',
  '.markdown',
  '.npmrc',
  '.nvmrc',
  '.patch',
  '.php',
  '.plist',
  '.properties',
  '.prettierrc',
  '.ps1',
  '.py',
  '.rb',
  '.rs',
  '.sass',
  '.scala',
  '.scss',
  '.sh',
  '.sln',
  '.sql',
  '.svelte',
  '.swift',
  '.tex',
  '.tsx',
  '.vb',
  '.vue',
  '.xaml',
  '.xsd',
  '.xsl',
  '.xslt',
  '.zsh'
])

const TEXT_FILE_NAMES = new Set([
  'changelog',
  'dockerfile',
  'gemfile',
  'license',
  'makefile',
  'procfile',
  'rakefile',
  'readme'
])

const SYNTAX_LANGUAGE_BY_EXTENSION: Readonly<Record<string, TextFilePreviewSyntaxLanguage>> = {
  '.bash': 'bash',
  '.c': 'c',
  '.cc': 'cpp',
  '.cfg': 'ini',
  '.cjs': 'javascript',
  '.cpp': 'cpp',
  '.cs': 'csharp',
  '.css': 'css',
  '.cxx': 'cpp',
  '.diff': 'diff',
  '.go': 'go',
  '.gql': 'graphql',
  '.graphql': 'graphql',
  '.h': 'c',
  '.hh': 'cpp',
  '.htm': 'xml',
  '.html': 'xml',
  '.hpp': 'cpp',
  '.hxx': 'cpp',
  '.ini': 'ini',
  '.java': 'java',
  '.js': 'javascript',
  '.json': 'json',
  '.jsonl': 'json',
  '.jsx': 'javascript',
  '.kt': 'kotlin',
  '.kts': 'kotlin',
  '.less': 'less',
  '.lua': 'lua',
  '.markdown': 'markdown',
  '.md': 'markdown',
  '.mdx': 'markdown',
  '.mjs': 'javascript',
  '.ndjson': 'json',
  '.patch': 'diff',
  '.php': 'php',
  '.py': 'python',
  '.rb': 'ruby',
  '.rs': 'rust',
  '.scss': 'scss',
  '.sh': 'bash',
  '.sql': 'sql',
  '.swift': 'swift',
  '.ts': 'typescript',
  '.tsx': 'typescript',
  '.vb': 'vbnet',
  '.xhtml': 'xml',
  '.xml': 'xml',
  '.xsd': 'xml',
  '.xsl': 'xml',
  '.xslt': 'xml',
  '.yaml': 'yaml',
  '.yml': 'yaml',
  '.zsh': 'bash'
}

const SYNTAX_LANGUAGE_BY_FILE_NAME: Readonly<Record<string, TextFilePreviewSyntaxLanguage>> = {
  makefile: 'makefile',
  readme: 'markdown'
}

const SYNTAX_LANGUAGE_BY_MIME_TYPE: Readonly<Record<string, TextFilePreviewSyntaxLanguage>> = {
  'application/graphql': 'graphql',
  'application/ecmascript': 'javascript',
  'application/javascript': 'javascript',
  'application/json': 'json',
  'application/ld+json': 'json',
  'application/ndjson': 'json',
  'application/sql': 'sql',
  'application/typescript': 'typescript',
  'application/x-httpd-php': 'php',
  'application/x-javascript': 'javascript',
  'application/x-ndjson': 'json',
  'application/x-sh': 'bash',
  'application/x-yaml': 'yaml',
  'application/xhtml+xml': 'xml',
  'application/xml': 'xml',
  'application/yaml': 'yaml',
  'text/css': 'css',
  'text/ecmascript': 'javascript',
  'text/html': 'xml',
  'text/ini': 'ini',
  'text/javascript': 'javascript',
  'text/less': 'less',
  'text/markdown': 'markdown',
  'text/scss': 'scss',
  'text/sql': 'sql',
  'text/typescript': 'typescript',
  'text/x-c': 'c',
  'text/x-c++': 'cpp',
  'text/x-csharp': 'csharp',
  'text/x-diff': 'diff',
  'text/x-go': 'go',
  'text/x-java-source': 'java',
  'text/x-kotlin': 'kotlin',
  'text/x-less': 'less',
  'text/x-lua': 'lua',
  'text/x-makefile': 'makefile',
  'text/x-markdown': 'markdown',
  'text/x-patch': 'diff',
  'text/x-php': 'php',
  'text/x-python': 'python',
  'text/x-ruby': 'ruby',
  'text/x-rust': 'rust',
  'text/x-scss': 'scss',
  'text/x-sh': 'bash',
  'text/x-shellscript': 'bash',
  'text/x-sql': 'sql',
  'text/x-swift': 'swift',
  'text/x-typescript': 'typescript',
  'text/x-vb': 'vbnet',
  'text/x-vbnet': 'vbnet',
  'text/x-yaml': 'yaml',
  'text/xml': 'xml',
  'text/yaml': 'yaml'
}

const BINARY_EXTENSIONS = new Set([
  '.7z',
  '.avi',
  '.bmp',
  '.dmg',
  '.doc',
  '.docx',
  '.eot',
  '.exe',
  '.gif',
  '.gz',
  '.heic',
  '.ico',
  '.jpeg',
  '.jpg',
  '.m4a',
  '.mkv',
  '.mov',
  '.mp3',
  '.mp4',
  '.odp',
  '.ods',
  '.odt',
  '.otf',
  '.pdf',
  '.png',
  '.ppt',
  '.pptx',
  '.rar',
  '.svg',
  '.tar',
  '.tif',
  '.tiff',
  '.ttf',
  '.wav',
  '.webm',
  '.webp',
  '.woff',
  '.woff2',
  '.xls',
  '.xlsx',
  '.zip'
])

/**
 * Checks whether file metadata safely identifies readable text content.
 * @param {string | null | undefined} fileName
 * @param {string | null | undefined} mimeType
 * @returns {boolean}
 */
export function isTextFilePreviewSupported(fileName: string | null | undefined, mimeType?: string | null): boolean {
  const extension = getFileExtension(fileName)
  const baseName = getFileBaseName(fileName)

  if (BINARY_EXTENSIONS.has(extension)) {
    return false
  }

  if (TEXT_EXTENSIONS.has(extension) || TEXT_FILE_NAMES.has(baseName) || baseName.startsWith('.env.')) {
    return true
  }

  const normalizedMimeType = normalizeMimeType(mimeType)

  if (!normalizedMimeType || GENERIC_MIME_TYPES.has(normalizedMimeType)) {
    return false
  }

  return isTextMimeType(normalizedMimeType)
}

/**
 * Gets the MIME type used to decode a supported text preview.
 * @param {string | null | undefined} fileName
 * @param {string | null | undefined} mimeType
 * @returns {string | null} A textual MIME type, or null for an unsupported file.
 */
export function getTextFilePreviewMimeType(
  fileName: string | null | undefined,
  mimeType?: string | null
): string | null {
  if (!isTextFilePreviewSupported(fileName, mimeType)) {
    return null
  }

  const extension = getFileExtension(fileName)
  const baseName = getFileBaseName(fileName)
  const normalizedMimeType = normalizeMimeType(mimeType)
  const extensionMimeType = TEXT_MIME_TYPE_BY_EXTENSION[extension]

  if (extensionMimeType) {
    return extensionMimeType
  }

  if (TEXT_EXTENSIONS.has(extension) || TEXT_FILE_NAMES.has(baseName) || baseName.startsWith('.env.')) {
    return 'text/plain'
  }

  if (normalizedMimeType && !GENERIC_MIME_TYPES.has(normalizedMimeType)) {
    return normalizedMimeType
  }

  return TEXT_MIME_TYPE_BY_EXTENSION[getFileExtension(fileName)] ?? 'text/plain'
}

/**
 * Gets the explicit grammar for a text preview without guessing from its content.
 * @param {string | null | undefined} fileName
 * @param {string | null | undefined} mimeType
 * @returns {TextFilePreviewSyntaxLanguage | null} A common Lowlight grammar, or null for plain text.
 */
export function getTextFilePreviewSyntaxLanguage(
  fileName: string | null | undefined,
  mimeType?: string | null
): TextFilePreviewSyntaxLanguage | null {
  const baseName = getFileBaseName(fileName)
  const extension = getFileExtension(fileName)
  const fileNameLanguage = SYNTAX_LANGUAGE_BY_FILE_NAME[baseName]

  if (fileNameLanguage) {
    return fileNameLanguage
  }

  const extensionLanguage = SYNTAX_LANGUAGE_BY_EXTENSION[extension]

  if (extensionLanguage) {
    return extensionLanguage
  }

  if (TEXT_EXTENSIONS.has(extension) || TEXT_FILE_NAMES.has(baseName) || baseName.startsWith('.env.')) {
    return null
  }

  const normalizedMimeType = normalizeMimeType(mimeType)

  if (normalizedMimeType.endsWith('+json')) {
    return 'json'
  }

  if (normalizedMimeType.endsWith('+xml')) {
    return 'xml'
  }

  return SYNTAX_LANGUAGE_BY_MIME_TYPE[normalizedMimeType] ?? null
}

/**
 * Gets a lower-case extension with its leading dot.
 * @param {string | null | undefined} fileName
 * @returns {string} The extension, or an empty string when none exists.
 */
function getFileExtension(fileName: string | null | undefined): string {
  const baseName = getFileBaseName(fileName)
  const extensionStart = baseName.lastIndexOf('.')

  return extensionStart >= 0 ? baseName.slice(extensionStart) : ''
}

/**
 * Gets a normalized file name without its path or URL suffix.
 * @param {string | null | undefined} fileName
 * @returns {string}
 */
function getFileBaseName(fileName: string | null | undefined): string {
  return (fileName ?? '').split(/[?#]/, 1)[0].split(/[\\/]/).pop()?.toLowerCase() ?? ''
}

/**
 * Normalizes MIME metadata without optional charset parameters.
 * @param {string | null | undefined} mimeType
 * @returns {string}
 */
function normalizeMimeType(mimeType?: string | null): string {
  return (mimeType ?? '').split(';', 1)[0].trim().toLowerCase()
}

/**
 * Checks whether one explicit MIME value describes text.
 * @param {string} mimeType
 * @returns {boolean}
 */
function isTextMimeType(mimeType: string): boolean {
  return (
    mimeType.startsWith('text/') ||
    TEXT_MIME_TYPES.has(mimeType) ||
    mimeType.endsWith('+json') ||
    mimeType.endsWith('+xml')
  )
}
